import { KNOWLEDGE_GRAPH_GROUP } from '#core/lib/expr/providers/groups.ts';
import type { DatasetProvider } from '#core/lib/expr/providers/types.ts';
import type { EvalDeps } from '#core/lib/expr/session/EvalDepsContext.ts';

export type ProviderEntry = readonly [string, DatasetProvider | undefined];

/**
 * A knowledge-graph view is describable-via-the-tool when its provider can
 * render a schema (`describe`) and it is not a raw CDF view facade (`cdf-view`
 * providers stay queryable but are not offered for description). This predicate
 * is the single source of truth for "which views may be described": both the
 * DescribeViews enum and the queryKnowledgeGraph describe-first guard compute
 * their view set from it, so the guard never demands describing a view the tool
 * refuses to offer.
 */
export const isDescribableKgProvider = ([, provider]: ProviderEntry): boolean =>
  provider?.describe !== undefined && provider?.id !== 'cdf-view';

/**
 * Resolve the knowledge-graph view names DescribeViews offers, drawn from the
 * provider registry and filtered to describable providers. `filter` defaults to
 * {@link isDescribableKgProvider}; callers may narrow further but should not
 * widen past it, or the guard and the enum will disagree.
 */
export function listDescribableViewNames(
  deps: Pick<EvalDeps, 'viewNames' | 'getProvider'>,
  filter: (entry: ProviderEntry) => boolean = isDescribableKgProvider,
): string[] {
  const names = [...new Set(deps.viewNames ?? [])];
  return names
    .map((name) => [name, deps.getProvider?.(KNOWLEDGE_GRAPH_GROUP, name)] as const)
    .filter((entry): entry is readonly [string, DatasetProvider] => entry[1] !== undefined)
    .filter(filter)
    .map(([name]) => name);
}
